import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Archive, Trash2, AlertTriangle, FileText, User, Calendar } from 'lucide-react';
import Header from './Header';
import BlacklistIncidentForm from './BlacklistIncidentForm';
import { useAuth } from '../contexts/AuthContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { archiveIncident, deleteIncident } from '../lib/blacklistService';

const BlacklistIncidentDetail = () => {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuth();
  const [incident, setIncident] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);

  // Parse navigation parameters
  const urlParams = new URLSearchParams(location.search);
  const fromOrder = urlParams.get('from') === 'order';
  const fromClient = urlParams.get('from') === 'client';
  const orderId = urlParams.get('orderId');
  const clientId = urlParams.get('clientId');

  useEffect(() => {
    fetchIncident();
  }, [incidentId]);

  const fetchIncident = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch incident data
      const incidentDoc = await getDoc(doc(db, 'blacklist_incidents', incidentId));
      
      if (!incidentDoc.exists()) {
        setError('Incidente no encontrado');
        return;
      }

      const incidentData = { id: incidentDoc.id, ...incidentDoc.data() };
      setIncident(incidentData);

      // Fetch client data if available
      if (incidentData.clienteId) {
        try {
          const clientDoc = await getDoc(doc(db, 'clients', incidentData.clienteId));
          if (clientDoc.exists()) {
            setClient({ id: clientDoc.id, ...clientDoc.data() });
          }
        } catch (error) {
          console.error('Error fetching client:', error);
          // Continue without client data
        }
      }
    } catch (error) {
      console.error('Error fetching incident:', error);
      setError('Error al cargar el incidente');
    } finally {
      setLoading(false);
    }
  };

  const canManageBlacklist = () => {
    return userRole === 'admin';
  };

  const buildBackUrl = () => {
    if (fromOrder && orderId) {
      return `/orders/${orderId}`;
    }
    if (fromClient && clientId) {
      return `/clients/${clientId}`;
    }
    return '/clients/blacklist';
  };

  const getBackText = () => {
    if (fromOrder) {
      return 'Volver a la orden';
    }
    if (fromClient) {
      return 'Volver al cliente';
    }
    return 'Volver a Lista Negra';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const getClientDisplayName = () => {
    if (!client) return incident?.clienteNombre || 'Cliente desconocido';
    
    if (client.nombreEmpresa) {
      const principal = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
      return principal?.nombre ? `${client.nombreEmpresa} - ${principal.nombre}` : client.nombreEmpresa;
    }
    
    const principal = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
    return principal?.nombre || 'Cliente';
  };

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleFormSuccess = () => {
    setSuccess('Incidente actualizado exitosamente');
    setShowEditForm(false);
    fetchIncident(); // Refresh data
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleArchive = async () => {
    if (!window.confirm('¿Está seguro de archivar este incidente?')) return;

    try {
      await archiveIncident(incident.id);
      setSuccess('Incidente archivado exitosamente');
      setTimeout(() => {
        navigate(buildBackUrl());
      }, 2000);
    } catch (error) {
      console.error('Error archiving incident:', error);
      setError('Error al archivar el incidente');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Está seguro de eliminar permanentemente este incidente?\\n\\nEsta acción no se puede deshacer.')) {
      return;
    }

    try {
      await deleteIncident(incident.id);
      setSuccess('Incidente eliminado permanentemente');
      setTimeout(() => {
        navigate(buildBackUrl());
      }, 2000);
    } catch (error) {
      console.error('Error deleting incident:', error);
      setError('Error al eliminar el incidente');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando incidente...
          </div>
        </div>
      </div>
    );
  }

  if (error && !incident) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto p-6">
          <div className="mb-6">
            <Link 
              to={buildBackUrl()}
              className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{getBackText()}</span>
            </Link>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to={buildBackUrl()}
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{getBackText()}</span>
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-6">
            {success}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Incidente de Lista Negra</h1>
                </div>
              </div>
              
              {canManageBlacklist() && incident?.estado === 'activo' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleEdit}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    title="Editar incidente"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleArchive}
                    className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
                    title="Archivar incidente"
                  >
                    <Archive className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Eliminar incidente"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                    Información del Incidente
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Motivo</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{incident?.motivoIncidente || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Detalles</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                        {incident?.detallesIncidente || 'Sin detalles adicionales'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Estado</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        incident?.estado === 'activo' 
                          ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {incident?.estado === 'activo' ? 'Activo' : 'Archivado'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                {incident?.montoDeuda && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Información Financiera</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Monto de Deuda</label>
                      <p className="mt-1 text-lg font-semibold text-red-600 dark:text-red-400">
                        ${incident.montoDeuda.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Client and Order Information */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <User className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                    Cliente y Orden
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cliente</label>
                      {client ? (
                        <Link 
                          to={`/clients/${client.id}`}
                          className="mt-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                        >
                          {getClientDisplayName()}
                        </Link>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{getClientDisplayName()}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Folio de Orden</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{incident?.ordenFolio || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                    Fechas
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fecha del Incidente</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(incident?.fechaIncidente)}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fecha de Registro</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(incident?.fechaCreacion)}</p>
                    </div>

                    {incident?.fechaArchivado && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fecha de Archivo</label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(incident.fechaArchivado)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BlacklistIncidentForm
        isOpen={showEditForm}
        onClose={() => setShowEditForm(false)}
        onSuccess={handleFormSuccess}
        editingIncident={incident}
        clientId={incident?.clienteId}
        clientName={getClientDisplayName()}
      />
    </div>
  );
};

export default BlacklistIncidentDetail;